// Başlıktaki "Hızlı işlem" menüsü. Girdiler kenar menüsünün görünürlük
// cevabından türer (useVisibleMenu); hedef sayfası rayda görünmeyen girdi
// gösterilmez. Yazma yetkisi gereken girdiler ek olarak hedef sayfanın kendi
// kapısını kullanır, böylece menü açılmayan bir formu önermez.

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { CalendarPlus, ChevronDown, Inbox, Megaphone, Plus, UserPlus, type LucideIcon } from "lucide-react";
import { Button, Popover } from "@novemai/components";

import { useScopeTier } from "@/api/use-scope-tier";
import { useVisibleMenu } from "@/components/use-visible-menu";
import { useAnnouncementAuthority } from "@/features/announcements/api";
import { NEW_REQUEST_PATH } from "@/features/requests/paths";
import { capabilityMatches, useEffectivePermissions } from "@/features/roles-permissions/use-effective-permissions";

interface QuickAction {
  readonly key: string;
  readonly labelKey: string;
  readonly href: string;
  readonly icon: LucideIcon;
}

const PANEL_WIDTH = 260;
const PANEL_GAP = 8;

/** Görünür hızlı işlemler; boş liste menünün hiç çizilmemesi demektir. */
export function useQuickActions(): readonly QuickAction[] {
  const menu = useVisibleMenu();
  const { capabilities } = useEffectivePermissions();
  const visible = new Set(menu.visibleKeys);
  const announcementAuthority = useAnnouncementAuthority(visible.has("announcements"));
  // Duyuru listesinin "Yeni duyuru" düğmesiyle aynı yüklem: şirket bazlı yetki matris hücresinden dardır.
  const canCreateAnnouncement = announcementAuthority.data?.some(company => company.actions.includes("create")) === true;
  const actions: QuickAction[] = [];
  if (visible.has("me-requests")) {
    actions.push({ key: "new-request", labelKey: "home_dashboard.quick.new_request", href: NEW_REQUEST_PATH, icon: Inbox });
    actions.push({ key: "file-leave", labelKey: "home_dashboard.quick.file_leave", href: "/me/leave/file", icon: CalendarPlus });
  }
  if (visible.has("people") && capabilityMatches(capabilities, "people.create")) {
    actions.push({ key: "new-employee", labelKey: "home_dashboard.quick.new_employee", href: "/people/create", icon: UserPlus });
  }
  if (visible.has("announcements") && canCreateAnnouncement) {
    actions.push({ key: "new-announcement", labelKey: "home_dashboard.quick.new_announcement", href: "/announcements/new", icon: Megaphone });
  }
  return actions;
}

export function QuickActionsMenu(): React.ReactElement | null {
  const { t } = useTranslation();
  const actions = useQuickActions();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  if (actions.length === 0) return null;

  const show = (): void => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect !== undefined) {
      setPosition({ top: rect.bottom + PANEL_GAP, left: Math.max(PANEL_GAP, rect.right - PANEL_WIDTH) });
    }
    setOpen(true);
  };

  return <>
    <Button ref={triggerRef} aria-haspopup="menu" aria-expanded={open} onClick={show}>
      <Plus size={15} aria-hidden="true" />{t("home_dashboard.quick.button")}<ChevronDown size={14} aria-hidden="true" />
    </Button>
    <Popover open={open} onClose={() => { setOpen(false); triggerRef.current?.focus(); }} aria-label={t("home_dashboard.quick.menu_label")}
      style={{ position: "fixed", top: position.top, left: position.left, width: PANEL_WIDTH }}>
      <ul className="m-0 list-none p-1.5">{actions.map(action => <li key={action.key}>
        <Link to={action.href} onClick={() => setOpen(false)}
          className="flex items-center gap-2.5 rounded-md px-3 py-2.5 text-[13.5px] text-[var(--fg-primary)] no-underline hover:bg-[var(--bg-subtle)] focus-visible:bg-[var(--bg-subtle)]">
          <action.icon size={15} aria-hidden="true" className="text-[var(--accent)]" />{t(action.labelKey)}
        </Link>
      </li>)}</ul>
    </Popover>
  </>;
}

/**
 * Çalışan ana sayfasında (emp-home) iki doğrudan eylem çizilir: "Yeni talep"
 * ve birincil "İzin al". Yönetim kapsamındaki çağıranın oluşturabileceği şey
 * çoktur; onlara aynı yerde menü açılır.
 */
export function HomeHeaderActions(): React.ReactElement | null {
  const { t } = useTranslation();
  const scopeTier = useScopeTier();
  const menu = useVisibleMenu();
  if (scopeTier === undefined) return null;
  if (scopeTier !== "employee_self") return <QuickActionsMenu />;
  if (!menu.visibleKeys.includes("me-requests")) return null;
  return <>
    <Button asChild variant="secondary"><Link to={NEW_REQUEST_PATH}><Inbox size={15} aria-hidden="true" />{t("home_dashboard.quick.new_request")}</Link></Button>
    <Button asChild><Link to="/me/leave/file"><CalendarPlus size={15} aria-hidden="true" />{t("home_dashboard.quick.file_leave")}</Link></Button>
  </>;
}
